import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Inject,
  NgModule,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { PortalModule } from '@angular/cdk/portal';
import { PortalService } from '@icilalune/utils';
import { SocialShareComponentModule } from '@icilalune/ui';
import {
  delay,
  filter,
  map,
  Observable,
  pairwise,
  startWith,
  take,
  tap,
} from 'rxjs';
import { ROUTE_CONTEXT } from '@icilalune/router';
import { RouteDataContext } from '@icilalune/types';
import { ComponentStore } from '@ngrx/component-store';
import { LetModule, PushModule } from '@ngrx/component';
import { IciLaLuneRouteComponent } from '@nx-icilalune/data/icilalune/types';
import { LoadingDirective } from '@nx-icilalune/icilalune/loading';
import { TranslocoModule } from '@ngneat/transloco';

interface ListDetailStructureStore {
  ready: boolean;
}

@Component({
  selector: 'sh-ui-list-detail-structure',
  template: `
    <div #contents class="b-contents">
      <div class="b-router-outlet">
        <router-outlet
          (activate)="onActivate($event)"
          (deactivate)="onDeActivate($event)"
        ></router-outlet>
      </div>
      <aside>
        <ng-container
          *ngrxLet="listDetailASidePortal$; let listDetailASidePortal"
        >
          <ng-container *ngIf="listDetailASidePortal">
            <ng-template
              [cdkPortalOutlet]="listDetailASidePortal"
            ></ng-template>
          </ng-container>
        </ng-container>
        <i2l-ui-social-share></i2l-ui-social-share>
      </aside>
    </div>
    <ng-container *transloco="let t">
      <ng-container *ngIf="t('loading.generic') as message">
        <ng-container
          *i2lLoading="
            !(ready$ | ngrxPush);
            loadingType: 'light';
            message: message
          "
        ></ng-container>
      </ng-container>
    </ng-container>
  `,
  styleUrls: ['./list-detail-structure.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ListDetailStructureComponent
  extends ComponentStore<ListDetailStructureStore>
  implements AfterViewInit
{
  readonly ready$ = this.select((s) => s.ready);

  @ViewChild('contents') contents: ElementRef<HTMLDivElement> | undefined;
  @ViewChild('loading', { read: ElementRef }) loading:
    | ElementRef<HTMLDivElement>
    | undefined;

  readonly getRouteDataContext = this.effect(
    (routeDataContext$: Observable<RouteDataContext>) => {
      return routeDataContext$.pipe(
        startWith(null),
        pairwise(),
        tap(([oldValue, newValue]) => {
          if (newValue && oldValue) {
            let prefix = oldValue.split(':').shift();
            if (prefix) {
              this.contents?.nativeElement.classList.remove(prefix);
            }
            this.contents?.nativeElement.classList.remove(
              oldValue.split(':').join('-')
            );

            prefix = newValue.split(':').shift();
            if (prefix) {
              this.contents?.nativeElement.classList.add(prefix);
            }
            this.contents?.nativeElement.classList.add(
              newValue.split(':').join('-')
            );
          }
        })
      );
    }
  );

  listDetailASidePortal$ = this.portalService.listDetailASidePortal$;

  setReady = this.effect((ready$: Observable<boolean>) => {
    return ready$.pipe(
      tap((ready) => {
        if (ready) {
          this.addReadyClass();
        } else {
          this.removeReadyClass();
        }
      })
    );
  });

  constructor(
    @Inject(ROUTE_CONTEXT)
    public routeDataContext$: Observable<RouteDataContext>,
    private portalService: PortalService,
    private el: ElementRef<HTMLElement>
  ) {
    super({ ready: false });
    this.getRouteDataContext(this.routeDataContext$);
  }

  onActivate(component: IciLaLuneRouteComponent) {
    if (component.ready$) {
      this.patchState(
        component.ready$
          .pipe(
            delay(100),
            filter((ready) => !!ready),
            take(1)
          )
          .pipe(
            map((ready) => {
              return { ready };
            })
          )
      );
    } else {
      this.patchState({ ready: true });
    }
  }

  onDeActivate(component: IciLaLuneRouteComponent) {
    this.patchState({ ready: false });
  }

  addReadyClass() {
    if (this.contents) {
      this.contents.nativeElement.classList.add('ready');
    }
    if (this.loading) {
      this.loading.nativeElement.classList.remove('loading');
    }
  }

  removeReadyClass() {
    if (this.contents) {
      this.contents.nativeElement.classList.remove('ready');
    }
    if (this.loading) {
      this.loading.nativeElement.classList.add('loading');
    }
  }

  ngAfterViewInit(): void {
    this.getRouteDataContext(this.routeDataContext$);
    this.setReady(this.ready$);
  }
}

@NgModule({
  imports: [
    CommonModule,
    RouterModule,
    PortalModule,
    SocialShareComponentModule,
    LetModule,
    PushModule,
    LoadingDirective,
    TranslocoModule,
  ],
  declarations: [ListDetailStructureComponent],
  exports: [ListDetailStructureComponent],
})
export class ListDetailStructureComponentModule {}
